// The acting-role controls: the header switcher (with its always-visible chip), the banner with a
// one-click "stop acting", and the help menu that reopens a role's walkthrough. The server holds the
// acting state; every start and stop is in the access log.
import * as Dropdown from "@radix-ui/react-dropdown-menu";
import { BookOpen, Check, ChevronDown, CircleHelp, Home, PlayCircle, Sparkles, UserRoundCog } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { actingBannerText, actingChipText, startActing, stopActing, switchableSeats, useMyActing } from "../lib/acting";
import { HELP_LEVEL_OPTIONS, helpLevelStore, setHelpLevel } from "../lib/prefs";
import { actingSeat, realRole, roleLabel } from "../lib/roles";
import type { Me } from "../lib/types";
import { tooltipFor } from "../lib/walkthroughs";
import { Button, cx, Tip } from "./ui";

const item = "focus-ring flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-[14px] text-ink outline-none data-[highlighted]:bg-hover data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50";

async function run(action: () => Promise<void>, failed: string) {
  try {
    await action();
  } catch (e) {
    toast.error(failed, { description: (e as Error).message });
  }
}

/** Header role switcher. Shown only to people who may act in a seat (or are acting now). */
export function RoleSwitcher({ me }: { me: Me }) {
  const acting = actingSeat(me);
  const mine = useMyActing();
  const seats = switchableSeats(mine.data);
  if (!acting && !seats.length) return null;
  const chip = actingChipText(acting);
  return (
    <Dropdown.Root>
      <Tip content={tooltipFor("role-switcher", me.user.role)} side="bottom">
        <Dropdown.Trigger
          data-tour="role-switcher"
          aria-label={acting ? `Acting as ${acting.label}. Switch role` : "Switch role"}
          className={cx(
            "focus-ring inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-semibold whitespace-nowrap",
            acting ? "bg-[#FFC266] text-navy-950" : "border border-white/25 text-white/85 hover:bg-white/10",
          )}
        >
          <UserRoundCog size={13} aria-hidden />
          {chip ?? "Act as"}
          <ChevronDown size={13} aria-hidden />
        </Dropdown.Trigger>
      </Tip>
      <Dropdown.Portal>
        <Dropdown.Content align="end" sideOffset={8} className="z-50 w-72 rounded-[10px] border border-line bg-raised p-1 shadow-[var(--shadow-2)]">
          <Dropdown.Label className="eyebrow px-2 py-1 text-ink-3">Act in a seat</Dropdown.Label>
          {seats.map((s) => (
            <Dropdown.Item key={s.role} className={item} onSelect={() => void run(() => startActing(s.role), `Couldn't switch to ${s.label}`)}>
              <span className="inline-flex w-9 justify-center rounded bg-sunken px-1 text-[11px] font-bold text-ink-2">{s.short}</span>
              {s.label}
            </Dropdown.Item>
          ))}
          {!seats.length ? <div className="px-2 py-1.5 text-[13px] text-ink-3">No other seats granted to you.</div> : null}
          {acting ? (
            <>
              <Dropdown.Separator className="my-1 h-px bg-subtle" />
              <Dropdown.Item className={item} onSelect={() => void run(() => stopActing(), "Couldn't stop acting")}>
                Back to my own role ({roleLabel(realRole(me))})
              </Dropdown.Item>
            </>
          ) : null}
          <Dropdown.Separator className="my-1 h-px bg-subtle" />
          <div className="px-2 py-1.5 text-[12px] leading-[17px] text-ink-3">
            A superadmin decides who may act as what. LoanShip records you plus the seat on everything you do; nobody approves their own proposal in any seat.
          </div>
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
}

/** Always-visible banner while acting, with a one-click way back. */
export function ActingBanner({ me }: { me: Me }) {
  const [busy, setBusy] = useState(false);
  const text = actingBannerText(me);
  if (!text) return null;
  return (
    <div role="region" aria-label="Acting role" className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-warning/40 bg-warning-bg px-3 py-1.5 text-[13px] text-warning md:px-5">
      <span className="font-semibold">{actingChipText(me.user.acting)}</span>
      <span className="min-w-0 flex-1">{text}</span>
      <Tip content={tooltipFor("stop-acting", me.user.role)}>
        <Button
          size="sm"
          data-tour="stop-acting"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            void run(() => stopActing(), "Couldn't stop acting").finally(() => setBusy(false));
          }}
        >
          Stop acting
        </Button>
      </Tip>
    </div>
  );
}

/** Help menu: open the Cliff-Notes training manual, reopen this role's walkthrough, or adjust Tooltip & Help Level. */
export function HelpMenu({ me }: { me: Me }) {
  const navigate = useNavigate();
  const helpLevel = helpLevelStore.use();
  return (
    <Dropdown.Root>
      <Tip
        content={tooltipFor("help-menu", me.user.role)}
        guide="Open the 7-module Cliff-Notes Training Manual or switch between Guided, Standard, and Minimal tooltip levels."
        side="bottom"
      >
        <Dropdown.Trigger
          data-tour="help-menu"
          aria-label={`Help and training (Tooltip level: ${helpLevel})`}
          className={cx(
            "focus-ring relative inline-flex h-8 items-center justify-center gap-1 rounded-full px-2 text-white/85 hover:bg-white/10",
            helpLevel === "guided" && "bg-white/15 text-white",
          )}
        >
          <CircleHelp size={17} aria-hidden />
          {helpLevel === "guided" ? (
            <span className="hidden text-[11px] font-semibold text-green-400 lg:inline">Guided</span>
          ) : null}
        </Dropdown.Trigger>
      </Tip>
      <Dropdown.Portal>
        <Dropdown.Content align="end" sideOffset={8} className="z-50 w-72 rounded-[10px] border border-line bg-raised p-1 shadow-[var(--shadow-2)]">
          <Dropdown.Label className="eyebrow px-2 py-1 text-ink-3">Speed-Learn & Walkthroughs</Dropdown.Label>
          <Dropdown.Item className={item} onSelect={() => void navigate("/training")}>
            <BookOpen size={14} className="text-accent" aria-hidden />
            <span className="flex-1 font-medium">Training Manual (Cliff Notes)</span>
            <span className="rounded bg-sunken px-1.5 py-0.5 text-[10px] font-bold text-ink-2">v1</span>
          </Dropdown.Item>
          <Dropdown.Item className={item} onSelect={() => void navigate("/manual")}>
            <BookOpen size={14} aria-hidden /> User manual & role playbooks
          </Dropdown.Item>
          <Dropdown.Item className={item} onSelect={() => void navigate("/role?tour=1")}>
            <PlayCircle size={14} aria-hidden /> Take the {roleLabel(me.user.role).toLowerCase()} tour
          </Dropdown.Item>
          <Dropdown.Item className={item} onSelect={() => void navigate("/role")}>
            <Home size={14} aria-hidden /> Role home
          </Dropdown.Item>
          <Dropdown.Separator className="my-1 h-px bg-subtle" />
          <Dropdown.Label className="eyebrow flex items-center gap-1.5 px-2 py-1 text-ink-3">
            <Sparkles size={12} aria-hidden /> Tooltips & Help Level
          </Dropdown.Label>
          {HELP_LEVEL_OPTIONS.map((opt) => (
            <Dropdown.Item
              key={opt.value}
              className="focus-ring flex cursor-pointer items-start gap-2 rounded-md px-2 py-1.5 text-ink outline-none data-[highlighted]:bg-hover"
              onSelect={(e) => {
                e.preventDefault();
                setHelpLevel(opt.value);
              }}
            >
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-medium text-ink">{opt.label}</div>
                <div className="text-[11px] leading-4 text-ink-3">{opt.description}</div>
              </div>
              {helpLevel === opt.value ? <Check size={14} className="mt-0.5 shrink-0 text-accent" /> : null}
            </Dropdown.Item>
          ))}
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
}
